import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  PersonSearchOutlined as PersonSearchOutlinedIcon,
  SaveOutlined as SaveOutlinedIcon,
  TuneOutlined as TuneOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  CloseOutlined as CloseOutlinedIcon,
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import PageContainer from '../common/PageContainer';

const DEFAULT_SCHOOL_ID = '1';

const LeaveBalanceManager = ({ schoolId: propSchoolId }) => {
  const theme = useTheme();
  const [schoolId, setSchoolId] = useState(null);
  const [currentAcademicYear, setCurrentAcademicYear] = useState('');

  const [employees, setEmployees] = useState([]);
  const [leaveCategories, setLeaveCategories] = useState([]);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);

  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [rowDraft, setRowDraft] = useState({});
  const [rowSavingId, setRowSavingId] = useState(null);

  const [searchText, setSearchText] = useState('');

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    leaveTypeId: '',
    typeName: '',
    numberAvailable: 0,
    academicYear: '',
    isPaid: false,
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const resolvedSchoolId =
      propSchoolId ||
      localStorage.getItem('schoolId') ||
      DEFAULT_SCHOOL_ID;
    const resolvedYear =
      localStorage.getItem('academicYear') ||
      new Date().getFullYear().toString();

    setSchoolId(String(resolvedSchoolId));
    setCurrentAcademicYear(resolvedYear);
  }, [propSchoolId]);

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchData = useCallback(async () => {
    if (!schoolId) {
      return;
    }

    setIsLoadingData(true);
    setError(null);

    try {
      const [employeesResponse, categoriesResponse] = await Promise.all([
        axiosInstance.get('/api/employees/leave-balance/all', {
          headers: { schoolId },
        }),
        axiosInstance.get('/api/leave-categories', {
          headers: { schoolId },
        }),
      ]);

      const employeesData = Array.isArray(employeesResponse.data)
        ? employeesResponse.data
        : [];
      const categoriesData = Array.isArray(categoriesResponse.data)
        ? categoriesResponse.data
        : [];

      setEmployees(employeesData);
      setLeaveCategories(categoriesData);
    } catch (err) {
      setError(err);
      showNotification(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load leave balance data.',
        'error'
      );
    } finally {
      setIsLoadingData(false);
    }
  }, [schoolId, showNotification]);

  useEffect(() => {
    if (schoolId) {
      fetchData();
    }
  }, [fetchData, schoolId]);

  const sortedCategories = useMemo(
    () =>
      [...leaveCategories].sort((a, b) =>
        String(a.typeName || '').localeCompare(String(b.typeName || ''))
      ),
    [leaveCategories]
  );

  const filteredEmployees = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) {
      return employees;
    }

    return employees.filter((employee) => {
      const name = String(employee.employeeName || '').toLowerCase();
      const code = String(employee.employeeCode || '').toLowerCase();
      return name.includes(keyword) || code.includes(keyword);
    });
  }, [employees, searchText]);

  const summaryStats = useMemo(() => {
    let totalConfigured = 0;
    let totalUsed = 0;

    employees.forEach((employee) => {
      (employee.leaveBalances || []).forEach((balance) => {
        totalConfigured += Number(balance.availableLeaves || 0);
        totalUsed += Number(balance.usedLeaves || 0);
      });
    });

    return {
      employeeCount: employees.length,
      categoryCount: leaveCategories.length,
      totalConfigured,
      totalUsed,
    };
  }, [employees, leaveCategories]);

  const resetCategoryForm = useCallback(() => {
    setCategoryForm({
      leaveTypeId: '',
      typeName: '',
      numberAvailable: 0,
      academicYear: currentAcademicYear,
      isPaid: false,
    });
  }, [currentAcademicYear]);

  const openCreateCategoryDialog = useCallback(() => {
    setSelectedCategory(null);
    resetCategoryForm();
    setCategoryDialogOpen(true);
  }, [resetCategoryForm]);

  const openEditCategoryDialog = useCallback(
    (category) => {
      setSelectedCategory(category);
      setCategoryForm({
        leaveTypeId: category.leaveTypeId,
        typeName: category.typeName || '',
        numberAvailable: Number(category.numberAvailable || 0),
        academicYear: category.academicYear || currentAcademicYear,
        isPaid: Boolean(category.isPaid),
      });
      setCategoryDialogOpen(true);
    },
    [currentAcademicYear]
  );

  const closeCategoryDialog = useCallback(() => {
    setCategoryDialogOpen(false);
    setSelectedCategory(null);
  }, []);

  const handleCategoryFormChange = useCallback((field, value) => {
    setCategoryForm((prev) => ({
      ...prev,
      [field]:
        field === 'numberAvailable'
          ? Math.max(0, Number.parseInt(value, 10) || 0)
          : field === 'isPaid'
          ? Boolean(value)
          : value,
    }));
  }, []);

  const saveCategory = useCallback(async () => {
    if (!categoryForm.typeName?.trim()) {
      showNotification('Leave type name is required.', 'warning');
      return;
    }

    setIsSavingCategory(true);

    try {
      const payload = {
        ...(selectedCategory
          ? { leaveTypeId: Number(categoryForm.leaveTypeId) }
          : {}),
        typeName: categoryForm.typeName.trim(),
        numberAvailable: Number(categoryForm.numberAvailable || 0),
        academicYear:
          categoryForm.academicYear?.trim() || currentAcademicYear,
        isPaid: Boolean(categoryForm.isPaid),
      };

      if (selectedCategory) {
        await axiosInstance.put(
          `/api/leave-categories/${payload.leaveTypeId}`,
          payload,
          { headers: { schoolId } }
        );
      } else {
        await axiosInstance.post('/api/leave-categories', payload, {
          headers: { schoolId },
        });
      }

      closeCategoryDialog();
      await fetchData();
      showNotification(
        selectedCategory
          ? 'Leave category updated successfully.'
          : 'Leave category created successfully.',
        'success'
      );
    } catch (err) {
      showNotification(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save leave category.',
        'error'
      );
    } finally {
      setIsSavingCategory(false);
    }
  }, [
    categoryForm,
    closeCategoryDialog,
    currentAcademicYear,
    fetchData,
    schoolId,
    selectedCategory,
    showNotification,
  ]);

  const deleteCategory = useCallback(
    async (category) => {
      if (!category?.leaveTypeId) {
        return;
      }

      const shouldDelete = window.confirm(
        `Delete category "${category.typeName}"? This cannot be undone.`
      );
      if (!shouldDelete) {
        return;
      }

      setIsDeletingCategory(true);
      try {
        await axiosInstance.delete(
          `/api/leave-categories/${category.leaveTypeId}`,
          {
            headers: { schoolId },
          }
        );
        await fetchData();
        showNotification('Leave category deleted successfully.', 'success');
      } catch (err) {
        showNotification(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to delete leave category.',
          'error'
        );
      } finally {
        setIsDeletingCategory(false);
      }
    },
    [fetchData, schoolId, showNotification]
  );

  const startRowEdit = useCallback(
    (employee) => {
      if (!employee?.employeeId) {
        return;
      }

      const initialDraft = {};
      sortedCategories.forEach((category) => {
        const existingBalance = (employee.leaveBalances || []).find(
          (balance) => balance.leaveTypeId === category.leaveTypeId
        );
        initialDraft[category.leaveTypeId] = Number(
          existingBalance?.availableLeaves || 0
        );
      });

      setEditingEmployeeId(employee.employeeId);
      setRowDraft(initialDraft);
    },
    [sortedCategories]
  );

  const cancelRowEdit = useCallback(() => {
    setEditingEmployeeId(null);
    setRowDraft({});
  }, []);

  const updateDraftValue = useCallback((leaveTypeId, value) => {
    const parsed = Math.max(0, Number.parseInt(value, 10) || 0);
    setRowDraft((prev) => ({ ...prev, [leaveTypeId]: parsed }));
  }, []);

  const buildRowValidation = useCallback(
    (employee) => {
      const errors = [];
      const payloads = [];

      sortedCategories.forEach((category) => {
        const existingBalance = (employee.leaveBalances || []).find(
          (balance) => balance.leaveTypeId === category.leaveTypeId
        ) || { availableLeaves: 0, usedLeaves: 0 };

        const draftValue = Number(
          rowDraft[category.leaveTypeId] ?? existingBalance.availableLeaves
        );

        if (draftValue > Number(category.numberAvailable || 0)) {
          errors.push(
            `${category.typeName}: total cannot exceed max ${category.numberAvailable}.`
          );
        }

        if (draftValue < Number(existingBalance.usedLeaves || 0)) {
          errors.push(
            `${category.typeName}: total cannot be lower than used ${existingBalance.usedLeaves}.`
          );
        }

        if (draftValue !== Number(existingBalance.availableLeaves || 0)) {
          payloads.push({
            numberAvailable: draftValue,
            employeeId: employee.employeeId,
            leaveTypeId: category.leaveTypeId,
            academicYear: category.academicYear || currentAcademicYear,
          });
        }
      });

      return { errors, payloads };
    },
    [currentAcademicYear, rowDraft, sortedCategories]
  );

  const hasRowChanges = useMemo(() => {
    if (!editingEmployeeId) {
      return false;
    }

    const employee = employees.find(
      (item) => item.employeeId === editingEmployeeId
    );

    if (!employee) {
      return false;
    }

    return sortedCategories.some((category) => {
      const existingBalance = (employee.leaveBalances || []).find(
        (balance) => balance.leaveTypeId === category.leaveTypeId
      );
      const baseValue = Number(existingBalance?.availableLeaves || 0);
      const draftValue = Number(rowDraft[category.leaveTypeId] ?? baseValue);
      return draftValue !== baseValue;
    });
  }, [editingEmployeeId, employees, rowDraft, sortedCategories]);

  const saveRow = useCallback(async () => {
    if (!editingEmployeeId) {
      return;
    }

    const employee = employees.find(
      (item) => item.employeeId === editingEmployeeId
    );

    if (!employee) {
      showNotification('Employee not found.', 'error');
      return;
    }

    const { errors, payloads } = buildRowValidation(employee);

    if (errors.length > 0) {
      showNotification(errors[0], 'error');
      return;
    }

    if (payloads.length === 0) {
      showNotification('No leave changes to save.', 'info');
      return;
    }

    setRowSavingId(editingEmployeeId);

    try {
      await Promise.all(
        payloads.map((payload) =>
          axiosInstance.post(
            `/api/employees/leave-balance/${editingEmployeeId}`,
            payload,
            { headers: { schoolId } }
          )
        )
      );

      await fetchData();
      setEditingEmployeeId(null);
      setRowDraft({});
      showNotification('Employee leave balance updated.', 'success');
    } catch (err) {
      showNotification(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to save employee leave balance.',
        'error'
      );
    } finally {
      setRowSavingId(null);
    }
  }, [
    buildRowValidation,
    editingEmployeeId,
    employees,
    fetchData,
    schoolId,
    showNotification,
  ]);

  const isBusy =
    isLoadingData || isSavingCategory || isDeletingCategory || !!rowSavingId;

  const headerActions = (
    <Stack direction="row" spacing={1}>
      <Button
        variant="text"
        size="small"
        onClick={fetchData}
        disabled={isBusy}
        sx={{
          borderRadius: 0,
          px: 1.4,
          fontWeight: 600,
        }}
      >
        Refresh Data
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={openCreateCategoryDialog}
        disabled={isBusy || !!editingEmployeeId}
        sx={{
          borderRadius: 0,
          px: 1.45,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }}
      >
        Add Leave Type
      </Button>
    </Stack>
  );

  const summaryItems = [
    {
      key: 'employees',
      icon: <Groups2OutlinedIcon fontSize="small" />,
      label: 'Employees',
      value: summaryStats.employeeCount,
    },
    {
      key: 'leaveTypes',
      icon: <TuneOutlinedIcon fontSize="small" />,
      label: 'Leave Types',
      value: summaryStats.categoryCount,
    },
    {
      key: 'configured',
      icon: <CheckCircleOutlineIcon fontSize="small" />,
      label: 'Configured Leaves',
      value: summaryStats.totalConfigured,
    },
    {
      key: 'academicYear',
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
      label: 'Academic Year',
      value: currentAcademicYear || 'N/A',
    },
  ];

  const tableHeaderCellSx = {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.72)}`,
    py: 0.9,
    px: 1.05,
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
    bgcolor: 'transparent',
    whiteSpace: 'nowrap',
  };

  const tableBodyCellSx = {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.56)}`,
    py: 0.68,
    px: 1.05,
    fontSize: '0.79rem',
    color: 'text.primary',
  };

  return (
    <PageContainer
      sx={{
        width: '100%',
        gap: { xs: 1.4, md: 1.8 },
        animation: 'leaveBalanceScreenFade 220ms ease-out',
        '@keyframes leaveBalanceScreenFade': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <PageHeader
        title="Leave Balance Control"
        subtitle="Configure leave policies and maintain employee leave allocations in one workspace."
        icon={<EventNoteOutlinedIcon fontSize="small" />}
        actions={headerActions}
        sx={{
          px: { xs: 1.1, md: 1.45 },
          py: { xs: 1.05, md: 1.3 },
          bgcolor: 'transparent',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.86)}`,
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        flexWrap="wrap"
        spacing={{ xs: 1.25, md: 1.55 }}
        sx={{
          px: { xs: 1.1, md: 1.45 },
          py: { xs: 1.35, md: 1.5 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
        }}
      >
        {summaryItems.map((item, index) => (
          <Stack
            key={item.key}
            direction="row"
            spacing={0.8}
            alignItems="center"
            sx={{
              minWidth: 180,
              pr: { md: 1.8 },
              borderRight:
                index < summaryItems.length - 1
                  ? `1px solid ${alpha(theme.palette.divider, 0.62)}`
                  : 'none',
            }}
          >
            <Box sx={{ color: 'primary.main', lineHeight: 0 }}>{item.icon}</Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.22 }}>
                {item.value}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
          pb: 0.2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: { xs: 1.1, md: 1.45 }, py: { xs: 1.05, md: 1.15 } }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Leave Type Registry
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Policy master list with yearly applicability and max allowance
          </Typography>
        </Stack>

        {isLoadingData ? (
          <Box sx={{ px: { xs: 1.1, md: 1.45 }, py: { xs: 1.3, md: 1.6 } }}>
            <Typography variant="body2" color="text.secondary">
              Loading leave categories...
            </Typography>
          </Box>
        ) : sortedCategories.length === 0 ? (
          <EmptyState
            title="No leave types configured"
            description="Create at least one leave type to start assigning balances to employees."
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openCreateCategoryDialog}
                disabled={isBusy}
              >
                Create Leave Type
              </Button>
            }
            sx={{ py: 3.4 }}
          />
        ) : (
          <TableContainer sx={{ maxHeight: 310 }}>
            <Table
              stickyHeader
              size="small"
              aria-label="leave category table"
              sx={{
                '& .MuiTableCell-head': tableHeaderCellSx,
                '& .MuiTableCell-body': tableBodyCellSx,
                '& .MuiTableCell-stickyHeader': {
                  bgcolor: theme.palette.background.default,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Leave Type</TableCell>
                  <TableCell align="center">Paid</TableCell>
                  <TableCell align="center">Max Allowed</TableCell>
                  <TableCell align="center">Academic Year</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCategories.map((category) => (
                  <TableRow key={category.leaveTypeId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {category.typeName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.73rem',
                          fontWeight: 700,
                          color: category.isPaid ? 'success.main' : 'warning.main',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        {category.isPaid ? 'Paid' : 'Unpaid'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {Number(category.numberAvailable || 0)}
                    </TableCell>
                    <TableCell align="center">
                      {category.academicYear || currentAcademicYear || 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Edit leave type">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditCategoryDialog(category)}
                              disabled={isBusy || !!editingEmployeeId}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete leave type">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteCategory(category)}
                              disabled={isBusy || !!editingEmployeeId}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box
        sx={{
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
          pb: 0.15,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: { xs: 1.1, md: 1.45 }, py: { xs: 1.05, md: 1.15 } }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Employee Leave Matrix
          </Typography>
          <TextField
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search employee name or code"
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonSearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        {isLoadingData ? (
          <Box sx={{ px: { xs: 1.1, md: 1.45 }, py: { xs: 1.3, md: 1.6 } }}>
            <Typography variant="body2" color="text.secondary">
              Loading employee leave balances...
            </Typography>
          </Box>
        ) : error ? (
          <EmptyState
            title="Unable to load leave balances"
            description={
              error?.response?.data?.message ||
              error?.message ||
              'Please retry in a moment.'
            }
            action={
              <Button
                variant="outlined"
                size="small"
                onClick={fetchData}
                disabled={isBusy}
              >
                Retry
              </Button>
            }
            sx={{ py: 3.4 }}
          />
        ) : sortedCategories.length === 0 ? (
          <EmptyState
            title="No leave types available"
            description="Add leave types first, then employee balances can be configured."
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openCreateCategoryDialog}
                disabled={isBusy}
              >
                Add Leave Type
              </Button>
            }
            sx={{ py: 3.4 }}
          />
        ) : filteredEmployees.length === 0 ? (
          <EmptyState
            title={searchText ? 'No matching employees' : 'No employees found'}
            description={
              searchText
                ? 'Try another keyword to find the employee record.'
                : 'Employee records are required to configure leave balances.'
            }
            sx={{ py: 3.4 }}
          />
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 365px)' }}>
            <Table
              stickyHeader
              size="small"
              aria-label="employee leave matrix"
              sx={{
                '& .MuiTableCell-head': tableHeaderCellSx,
                '& .MuiTableCell-body': tableBodyCellSx,
                '& .MuiTableCell-stickyHeader': {
                  bgcolor: theme.palette.background.default,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 210 }}>Employee</TableCell>
                  {sortedCategories.map((category) => (
                    <TableCell
                      key={category.leaveTypeId}
                      align="center"
                      sx={{ minWidth: 170 }}
                    >
                      <Stack spacing={0.2} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {category.typeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Max {category.numberAvailable}
                        </Typography>
                      </Stack>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ minWidth: 130 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((employee) => {
                  const isEditingRow = editingEmployeeId === employee.employeeId;
                  const isRowSaving = rowSavingId === employee.employeeId;

                  return (
                    <TableRow
                      key={employee.employeeId}
                      hover
                      sx={{
                        bgcolor: isEditingRow
                          ? alpha(theme.palette.primary.main, 0.11)
                          : 'transparent',
                        '&:hover': {
                          bgcolor: isEditingRow
                            ? alpha(theme.palette.primary.main, 0.14)
                            : alpha(theme.palette.primary.main, 0.035),
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {employee.employeeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.employeeCode || '-'}
                        </Typography>
                      </TableCell>

                      {sortedCategories.map((category) => {
                        const existingBalance = (employee.leaveBalances || []).find(
                          (balance) =>
                            balance.leaveTypeId === category.leaveTypeId
                        ) || { availableLeaves: 0, usedLeaves: 0 };

                        const displayTotal = Number(
                          isEditingRow
                            ? rowDraft[category.leaveTypeId] ??
                                existingBalance.availableLeaves
                            : existingBalance.availableLeaves
                        );
                        const used = Number(existingBalance.usedLeaves || 0);
                        const remaining = displayTotal - used;
                        const hasCellError =
                          isEditingRow &&
                          (displayTotal < used ||
                            displayTotal >
                              Number(category.numberAvailable || 0));

                        return (
                          <TableCell
                            key={category.leaveTypeId}
                            align="center"
                          >
                            {isEditingRow ? (
                              <TextField
                                type="number"
                                size="small"
                                value={displayTotal}
                                onChange={(event) =>
                                  updateDraftValue(
                                    category.leaveTypeId,
                                    event.target.value
                                  )
                                }
                                inputProps={{
                                  min: used,
                                  max: Number(category.numberAvailable || 0),
                                  style: { textAlign: 'center' },
                                }}
                                error={hasCellError}
                                helperText={
                                  hasCellError
                                    ? `Min ${used} / Max ${category.numberAvailable}`
                                    : undefined
                                }
                                sx={{
                                  width: 118,
                                  '& .MuiOutlinedInput-root': { borderRadius: 0 },
                                  '& .MuiFormHelperText-root': {
                                    mx: 0,
                                    mt: 0.45,
                                    textAlign: 'center',
                                    fontSize: '0.66rem',
                                  },
                                }}
                                disabled={isRowSaving}
                              />
                            ) : (
                              <Stack spacing={0.2} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {displayTotal}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={
                                    remaining < 0
                                      ? 'error.main'
                                      : 'text.secondary'
                                  }
                                >
                                  Used {used} / Left {remaining}
                                </Typography>
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}

                      <TableCell align="right">
                        {isEditingRow ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Save row">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={saveRow}
                                  disabled={!hasRowChanges || isRowSaving}
                                >
                                  <SaveOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancel edit">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={cancelRowEdit}
                                  disabled={isRowSaving}
                                >
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Tooltip title="Edit employee balances">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => startRowEdit(employee)}
                                disabled={
                                  !!editingEmployeeId ||
                                  isBusy
                                }
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog
        open={categoryDialogOpen}
        onClose={closeCategoryDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedCategory ? 'Edit Leave Type' : 'Create Leave Type'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.4} sx={{ pt: 0.8 }}>
            <TextField
              label="Leave Type Name"
              value={categoryForm.typeName || ''}
              onChange={(event) =>
                handleCategoryFormChange('typeName', event.target.value)
              }
              required
              fullWidth
            />

            <TextField
              label="Maximum Leave"
              type="number"
              value={categoryForm.numberAvailable ?? 0}
              onChange={(event) =>
                handleCategoryFormChange(
                  'numberAvailable',
                  event.target.value
                )
              }
              fullWidth
              inputProps={{ min: 0 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
            />

            <TextField
              label="Academic Year"
              value={categoryForm.academicYear || ''}
              onChange={(event) =>
                handleCategoryFormChange('academicYear', event.target.value)
              }
              placeholder={currentAcademicYear || '2025-2026'}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(categoryForm.isPaid)}
                  onChange={(event) =>
                    handleCategoryFormChange('isPaid', event.target.checked)
                  }
                />
              }
              label="Mark as paid leave"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={closeCategoryDialog}
            disabled={isSavingCategory}
            sx={{ borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveCategory}
            disabled={isSavingCategory || !categoryForm.typeName?.trim()}
            sx={{
              borderRadius: 0,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            {selectedCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default LeaveBalanceManager;
